/**
 * Visual tokens.
 *
 * Dark-first, and not as a style preference: the busiest hours of this job are after
 * sundown, and a white screen at 23:00 on Ibn Gvirol wrecks your night vision for the
 * next thirty seconds of riding. The palette is built for the dark surface first and
 * checked against it.
 */

/**
 * Sequential ramp for the demand overlay, low to high.
 *
 * Orange rather than the default sequential blue, for one specific reason: the
 * Mediterranean runs down the entire west edge of the working area and every basemap
 * renders it blue. A blue overlay against blue water is the one legibility failure this
 * map cannot afford. Orange is the documented second sequential hue and reads as
 * "hot" besides.
 *
 * Validated as a 6-step ordinal ramp against the #14141a map surface: monotone
 * lightness, all adjacent gaps ≥ 0.06, dark end at 2.35:1 (clears the 2:1 floor), hue
 * spread 13°.
 */
export const DEMAND_RAMP = [
  '#833f1c',
  '#a95120',
  '#cd6428',
  '#e28245',
  '#efa877',
  '#fbcdab',
] as const;

/**
 * Cells below this normalised score are not painted at all.
 *
 * A map where everything is coloured tells you nothing. Leaving the dead parts of the
 * city bare is what makes the live part legible at a glance, which is the only way this
 * gets read — at a glance, one-handed, stopped at a light.
 *
 * Recalibrated from 0.18 when the working area grew from central Tel Aviv to the whole
 * of Tel Aviv-Yafo plus Givatayim and Ramat Gan. Scores are normalised against the best
 * cell in the week, and that cell is still in the dense centre — so quadrupling the area
 * did not raise the ceiling, it just added a thousand cells below it. At 0.18 the map
 * painted 7% of cells at peak lunch and 0% late on a Saturday, and whole real
 * neighbourhoods — the Old North among them — were never painted at any hour of any day
 * despite having 150 venues open within six minutes.
 *
 * 0.08 paints roughly 15% at the two weekday peaks and 5% on a Shabbat afternoon.
 *
 * Lowering it costs nothing against the "a dark map is a real answer" principle, which
 * was the reason the floor existed. That principle is protected by the *scores*, not by
 * this threshold: at 04:00 on a Tuesday and 06:00 on a Saturday every cell scores
 * essentially zero, so those hours render black at 0.03 exactly as they did at 0.18.
 * The floor was hiding live neighbourhoods, not dead ones.
 */
export const DEMAND_FLOOR = 0.08;

export const COLORS = {
  surface: '#14141a',
  surfaceRaised: '#1f1f27',
  surfaceSunken: '#0e0e12',
  border: '#2e2e39',
  textPrimary: '#f4f4f2',
  textSecondary: '#a8a8b3',
  textMuted: '#71717f',
  accent: '#e28245',
  good: '#0ca30c',
  warning: '#eda100',
} as const;

/** Map the 0..1 normalised score onto a ramp step. */
export function rampColor(normalized: number): string {
  if (normalized < DEMAND_FLOOR) return 'transparent';
  const span = 1 - DEMAND_FLOOR;
  const position = (normalized - DEMAND_FLOOR) / span;
  const index = Math.min(DEMAND_RAMP.length - 1, Math.floor(position * DEMAND_RAMP.length));
  return DEMAND_RAMP[index]!;
}

/**
 * ── The first knob to turn ────────────────────────────────────────────────────
 *
 * Fill opacity of the demand hexes, 0–255.
 *
 * This matters more on Google than it did on the previous basemap. There, the grid could
 * be inserted *beneath* the street-label layers, so labels stayed crisp whatever the
 * fill did. deck.gl's Google overlay draws above the entire basemap, labels included,
 * and a raster map offers no way to interleave — so the only thing keeping street names
 * readable is that this number is low enough.
 *
 * 108/255 is about 42%: enough for the pattern to read at a glance, light enough that
 * Google's labels punch through. If names look washed out on a real screen, lower it.
 * If the grid looks too faint in daylight, raise it — and re-check the labels.
 */
export const OVERLAY_ALPHA = 108;

/** Tapped cell, lifted so the selection is unmistakable. */
export const OVERLAY_ALPHA_SELECTED = 190;

/**
 * Hex outlines carry more of the load here than they did before, for the same reason:
 * a crisp boundary lets the fill stay light without the grid becoming mush.
 */
export const OVERLAY_LINE_ALPHA = 210;
export const OVERLAY_LINE_WIDTH_M = 8;
export const OVERLAY_LINE_WIDTH_SELECTED_M = 22;

export type Rgba = [number, number, number, number];

/** Parse `#rrggbb` into its channels. */
export function hexToRgb(hex: string): [number, number, number] {
  const value = hex.replace('#', '');
  return [
    parseInt(value.slice(0, 2), 16),
    parseInt(value.slice(2, 4), 16),
    parseInt(value.slice(4, 6), 16),
  ];
}

/**
 * Ramp step as an RGBA array — deck.gl takes channel arrays, not CSS colour strings.
 *
 * Cells under DEMAND_FLOOR come back fully transparent rather than being filtered out
 * upstream, so the layer's data array stays index-stable across time-slider ticks.
 */
export function rampColorRgba(normalized: number, selected = false): Rgba {
  if (normalized < DEMAND_FLOOR) return [0, 0, 0, 0];
  const [r, g, b] = hexToRgb(rampColor(normalized));
  return [r, g, b, selected ? OVERLAY_ALPHA_SELECTED : OVERLAY_ALPHA];
}

/** Outline colour: the same ramp step, or white for the tapped cell. */
export function rampLineRgba(normalized: number, selected = false): Rgba {
  if (normalized < DEMAND_FLOOR) return [0, 0, 0, 0];
  if (selected) {
    const [r, g, b] = hexToRgb(COLORS.textPrimary);
    return [r, g, b, 255];
  }
  const [r, g, b] = hexToRgb(rampColor(normalized));
  return [r, g, b, OVERLAY_LINE_ALPHA];
}
